/**
 * Describes the array result returned by `toArray`.
 *
 * @example
 * ```ts
 * type Single = ToArray<string>;
 * // Single is [string]
 *
 * type Multiple = ToArray<ReadonlySet<number>>;
 * // Multiple is Array<number>
 * ```
 */
type ToArray<T> =
  T extends ReadonlyArray<infer Item>
    ? Array<Item>
    : T extends ReadonlySet<infer Item>
      ? Array<Item>
      : [T];

/**
 * Provides a type-narrowing version of `Array.prototype.includes`.
 *
 * @example
 * ```ts
 * const states = ['active', 'inactive'] as const;
 * const value: string = 'active';
 *
 * if (includes(states, value)) {
 *   // value is narrowed to 'active' | 'inactive'
 * }
 * ```
 */
function includes<T extends ReadonlyArray<U>, U>(
  arr: T,
  value: U,
): value is T[number] {
  return arr.includes(value);
}

/**
 * Checks whether a value is a plain object.
 *
 * Arrays, dates, maps, and sets return false.
 *
 * @example
 * ```ts
 * isObject({ name: 'Jane' }); // true
 * isObject(['Jane']); // false
 * isObject(new Date()); // false
 * ```
 */
function isObject(value: unknown): value is object {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Converts one value to a single-item array.
 *
 * Arrays are returned directly and set values are copied to a new array.
 *
 * @example
 * ```ts
 * toArray('ACTIVE'); // ['ACTIVE']
 * toArray(new Set(['ACTIVE'])); // ['ACTIVE']
 * toArray(['ACTIVE']); // ['ACTIVE']
 * ```
 */
function toArray<const T>(value: T): ToArray<T>;
function toArray(value: unknown): Array<unknown> {
  if (Array.isArray(value)) {
    return value;
  }

  if (value instanceof Set) {
    return [...value];
  }

  return [value];
}

/**
 * Returns the only array item directly.
 *
 * Arrays with multiple values remain arrays. An empty array remains empty.
 *
 * @example
 * ```ts
 * unwrapArray(['ACTIVE']); // 'ACTIVE'
 * unwrapArray(['ACTIVE', 'INACTIVE']); // ['ACTIVE', 'INACTIVE']
 * unwrapArray([]); // []
 * ```
 */
function unwrapArray<T>(arr: Array<T>): Array<T> | T {
  return arr.length > 1 ? arr : (arr[0] ?? []);
}

export { includes, isObject, toArray, unwrapArray };
